import { BasicInfo, Dest, ShippingDetail, TimeLine, TimeLineItem } from '@/types/result';
import * as df from 'date-fns';
import numeral from 'numeral';
import * as _ from 'lodash';

export const isEmptyString = (str: string): boolean => {
  let retVal = false;
  if (str && str.length > 0) {
    switch (str) {
      case 'N/A':
      case 'n/a':
      case 'null':
        retVal = false;
        break;
      default:
        retVal = true;
        break;
    }
  }

  return retVal;
};

export const distanceStringMeterToKm = (str: string | number): string => {
  if (typeof str === 'string') str = Number(str);
  let res = numeral(str).divide(1000).format('0,0.0');
  return str !== 0 ? res + 'km' : '--';
};

export const secondsToTimestamp = (str: string | number): string => {
  if ((str === '--' || !str) && str !== 0) return '--';
  const num = Number(str);
  const seconds = num % 60;
  const minutes = ((num - seconds) % (60 * 60)) / 60;
  const hours = (num - seconds - minutes * 60) / (60 * 60);

  return str !== 0 ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` : '--';
};

export const timeStringtoSecond = (str: string): number => {
  const tArr = str.split(':');
  return Number(tArr[0]) * 3600 + Number(tArr[1]) * 60 + Number(tArr[2]);
};

export const createTimelineMin = (basicInfo: BasicInfo, shippingList: ShippingDetail[]): TimeLine[] => {
  if (basicInfo && shippingList) {
    const timeLineLength = getTimelineLength(basicInfo.timeLine.timelineLength);
    const startTime = basicInfo.timeLine.startHour;
    const startMinuteOfDay = startTime.getHours() * 60 + startTime.getMinutes();

    // from/to 라벨은 차량이 몇 대든 동일(같은 시작시각 기준)하므로 차량마다 반복 계산하지 않고
    // 한 번만 만들어서 재사용한다. (기존엔 차량 수 × 분 수만큼 df.format/df.add를 호출했음)
    const fromLabels: string[] = new Array(timeLineLength);
    const toLabels: string[] = new Array(timeLineLength);
    for (let i = 0; i < timeLineLength; i++) {
      fromLabels[i] = df.format(df.add(startTime, { seconds: i * 60 }), 'HH:mm:ss');
      toLabels[i] = df.format(df.add(startTime, { seconds: (i + 1) * 60 - 1 }), 'HH:mm:ss');
    }

    const originList: TimeLine[] = shippingList.map((shippingDetail: ShippingDetail, slIndex: number) => {
      const timeLine: TimeLineItem[] = [];
      const color = shippingDetail.color;
      const car = shippingDetail.car;

      // 목적지 도착시각(분 오프셋)을 미리 인덱싱해서, 분마다 destList를 훑어 매칭하던
      // O(분 × 목적지수)를 O(분 + 목적지수)로 줄인다. arrivalTime은 'HH:mm' 형식.
      const destByMinuteIndex = new Map<number, Dest>();
      shippingDetail.destList?.forEach((dest: Dest) => {
        const [h, m] = dest.arrivalTime.split(':').map(Number);
        if (Number.isFinite(h) && Number.isFinite(m)) {
          destByMinuteIndex.set(h * 60 + m - startMinuteOfDay, dest);
        }
      });

      for (let timeIndex = 0; timeIndex < timeLineLength; timeIndex++) {
        const targetItem = destByMinuteIndex.get(timeIndex);
        const obj: TimeLineItem = {
          index: slIndex + '_' + timeIndex,
          color,
          car,
          from: fromLabels[timeIndex],
          to: toLabels[timeIndex],
          target: targetItem,
        };
        if (targetItem) {
          timeIndex += (targetItem.dwellTime.value ? targetItem.dwellTime.value / 60 : 1) - 1;
        }
        timeLine.push(obj);
      }
      const { destList, destLength, orderLength } = getTimeLineDestInfo(timeLine);
      return {
        index: slIndex,
        car,
        color,
        destList,
        destLength,
        orderLength,
        distance: shippingDetail.distance,
        time: shippingDetail.time,
        route: shippingDetail.route,
        reverse: shippingDetail.reverse,
        timelineList: timeLine,
      };
    });
    return originList;
  }
};

export const getTimeLineDestInfo = (
  timeLine: TimeLineItem[],
): { destList: Dest[]; destLength: number; orderLength: number } => {
  if (timeLine && timeLine.length > 0) {
    const temp: TimeLineItem[] = timeLine.filter((v: TimeLineItem) => v.target);
    const destList: Dest[] = _.map(temp, (v: TimeLineItem) => {
      return v.target;
    });

    return {
      destList,
      destLength: destList.length,
      orderLength: _.sumBy(destList, (v) => v.orderInfo?.length),
    };
  }
};

export const createTimeBase = (start: Date, end: Date, interval: number): any[] => {
  const retArr: any[] = [];
  if (start && end && interval) {
    let time = start;
    while (time < end) {
      retArr.push(time);
      time = df.add(time, { minutes: interval });
    }
  }
  return retArr;
};

export const getTimelineLength = (len: number) => {
  return len > 15 ? len : 16;
};

export const nearXFiveZeroMin = (param: Date): Date => {
  const min = df.getMinutes(param).toString();
  let toEndNum = Number(min[min.length - 1]);
  if (toEndNum !== 5) {
    toEndNum -= toEndNum > 5 ? toEndNum % 5 : toEndNum;
  }
  const calMin = min.length > 1 ? min[0] + toEndNum : toEndNum;
  param.setMinutes(Number(calMin));
  param.setSeconds(0);
  return param;
};

export const destListCarSeqUpdate = (destList: any[], targetId: number, updateCarSeq: number | null): any[] => {
  const retArr: any[] = _.map(destList, (v) => {
    if (v.seq !== targetId) {
      return v;
    } else {
      return {
        ...v,
        r_car_seq: updateCarSeq,
      };
    }
  });
  return retArr;
};
